import { useContext, useEffect, useMemo, useRef } from 'react';
import SelectableState from '../../../contexts/local/selectable/SelectableState';
import ProcessContext from '../../../contexts/process/ProcessContext';
import Loading from '../../ui-elements/Loading';
import EntitySelector from '../../ui-elements/EntitySelector';
import PanelSelector from '../transplanting/PanelSelector';
import OccupiedTraySlotSelector from './tray/OccupiedTraySlotSelector';
import ConfirmPlantDiscarding from './tray/ConfirmPlantDiscarding';
import PanelSelection from './panel/PanelSelection';
import ConfirmPanelDiscarding from './panel/ConfirmPanelDiscarding';

const Discarding = () => {
  const {
    occupancyArray,
    additionalProcessInformation = {},
    requestedInformationType,
    processFinished,
    finishProcess,
  } = useContext(ProcessContext);

  const completingRef = useRef(false);

  const { processStep, endOfLife, option } = additionalProcessInformation  ?? {};
  const totalSlots = parseInt(import.meta.env.VITE_PANELSETTINGS_TOTALPLANTSLOTSPERCHANNEL, 10);

  useEffect(() => {
    return () => {
      if (!completingRef.current) {
        finishProcess();
      }
    }
  }, []);

  // Compute availableItems for tray or panel
  const availableItems = useMemo(() => {
    if (processStep !== 2) return [];

    // In the end of life process, we only want to be able to select plants that have been harvested at least once.
    if (endOfLife && option === 'panel') {
      return occupancyArray
      .map((channel) =>
        channel.plantSlotBlocks
          .filter((block) => block.isOccupied && block.harvestCount > 0)
          .map((block) => block.startPlantSlotOrdinalNumber)
      )
      .flat()
    }

    if (option === 'tray') {
      return occupancyArray
        .filter(item => item && !item.isAvailable)
        .map(item => item.index);
    } else if (option === 'panel') {
      return occupancyArray
      .map((channel) =>
        channel.plantSlotBlocks
          .filter((block) => block.isOccupied)
          .map((block) => block.startPlantSlotOrdinalNumber)
      )
      .flat()
    }
    return [];
  }, [occupancyArray, processStep, option, totalSlots]);

  const renderStep = () => {
    if (additionalProcessInformation == null || processStep == null) {
      return <Loading fullScreen />;
    }
    
    switch (processStep) {
      case 1:
        // Entity selection (tray / panel)
        return endOfLife
          ? <PanelSelector forwardTo="/discarding" />
          : <EntitySelector forwardTo="/discarding" />;

      case 2:
        // Confirm sub-step
        if (requestedInformationType === 7) {
          return endOfLife
            ? <ConfirmPanelDiscarding forwardTo="/" completingRef={completingRef} />
            : <ConfirmPlantDiscarding forwardTo="/" completingRef={completingRef} />;
        }
        // Slot selection
        if (option === 'tray') {
          return (
            <SelectableState availableItems={availableItems}>
              <OccupiedTraySlotSelector forwardTo="/discarding" />
            </SelectableState>
          );
        } else if (option === 'panel') {
          return (
            <SelectableState availableItems={availableItems}>
              <PanelSelection forwardTo="/discarding" availableItems={availableItems} />
            </SelectableState>
          );
        }
        break;
      case 3:
        return option === 'panel'
        ? <ConfirmPanelDiscarding forwardTo="/" completingRef={completingRef} />
        : <ConfirmPlantDiscarding forwardTo="/" completingRef={completingRef} />;
    }

    // Process finished?
    if (processFinished || requestedInformationType === 7) {
      if(endOfLife|| option === 'panel'){
        return <ConfirmPanelDiscarding forwardTo="/" completingRef={completingRef}/>
      }
      return <ConfirmPlantDiscarding forwardTo="/" completingRef={completingRef} />;
    }
  };

  return (
    <div  id="discarding-container" 
          className={
            `process-container${endOfLife || processStep == 3 ? ' panel-container' : ''}`
          } 
          style={{ height: '100%' }}>
      {renderStep()}
    </div>
  );
};

export default Discarding;
